
import React, { useState,useEffect } from 'react';
import laymapJson from '../data/layermap.json';
import './DrcViewer.css';
import UploadIcon from '@mui/icons-material/Upload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { BackspaceOutlined, HighlightOutlined } from '@mui/icons-material';
import { RestartAltRounded } from '@mui/icons-material';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import HighlightIcon from '@mui/icons-material/Highlight';

const ViolationItem = ({ violation, index, dbuu ,groupViolations}) => {
  const handleViolationClick = () => {
    if (window.canvasEngine?.drc?.drawViolation) {
      const scaled = {
        ...violation,
        location: violation.location?.map(([x, y]) => [
        (x / dbuu) * (1/window.newUserSize || 1),
        (y / dbuu) * (1/window.newUserSize || 1)
      ])
    };
    // Scale entire group
      const scaledGroup = groupViolations.map(v => ({
        ...v,
        location: v.location?.map(([x, y]) => [
          (x / dbuu) * (1/window.newUserSize || 1),
          (y / dbuu) * (1/window.newUserSize || 1)
        ])
      }));
      window.canvasEngine.drc.drawViolation(scaled, scaledGroup);
      window.canvasEngine.smoothZoomToViolation(scaled); // 👈 smooth zoom instead of jump
    } else {
      console.error('CanvasEngine not found or drawViolation method missing!');
    }
  };

  // const coordinatesTooltip = violation.location?.map(point => `(${point.join(', ')})`).join(' → ') || '';
  const coordinatesTooltip = violation.location?.map(
  ([x, y]) => `(${((x / dbuu) * (1/window.newUserSize || 1)).toFixed(3)}, ${((y / dbuu) * (1/window.newUserSize || 1)).toFixed(3)})`
).join(' → ') || '';


  return (
    <div className="violation-entry" title={coordinatesTooltip}
        onClick={handleViolationClick}>
      <span
        className="violation-index"
      >
        {index + 1}
      </span>
    </div>
  );
};

const RuleGroup = ({ group, dbuu, groupId, expandedLayers, setExpandedLayers }) => {
  const isExpanded = expandedLayers.has(groupId);

  const toggleExpand = () => {
    const newSet = new Set(expandedLayers);
    isExpanded ? newSet.delete(groupId) : newSet.add(groupId);
    setExpandedLayers(newSet);
  };

  return (
    <div className="rule-group">
      <div className="rule-group-header" onClick={toggleExpand}>
        <span className="layer-info">
          {isExpanded ? '-' : '+'} Layer: {group.layer_name} {group.datatype_name} ({group.layer_number}/{group.datatype_number})
        </span>
        <span className="value-info">Value: {group.value}</span>
      </div>
      {isExpanded && (
        <>
          <div className="rule-group-description">{group.explanation}</div>
          <div className="violations-list">
            {group.violations?.map((violation, idx) => (
              <ViolationItem key={idx} violation={violation} index={idx} dbuu={dbuu} groupViolations={group.violations} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const RuleItem = ({ ruleName, ruleGroups, expandedRules, setExpandedRules, dbuu, expandedLayers, setExpandedLayers }) => {
  const isExpanded = expandedRules.has(ruleName);
  const toggleExpand = () => {
    const newSet = new Set(expandedRules);
    isExpanded ? newSet.delete(ruleName) : newSet.add(ruleName);
    setExpandedRules(newSet);
  };

  const totalViolations = ruleGroups.reduce((sum, g) => sum + (g.violations?.length || 0), 0);

  return (
    <div className="rule-entry">
      <div className="rule-header" onClick={toggleExpand} title={`Rule: ${ruleName}`}>
        <span className="toggle-indicator">{isExpanded ? '-' : '+'}</span>
        <span className="rule-title">{ruleName}</span>
        <span className="rule-violation-count">{totalViolations}</span>
      </div>
      {isExpanded && (
        <div className="rule-groups-container">
          {ruleGroups.map((group, idx) => {
            const groupId = `${ruleName}__${idx}`;
            return (
              <RuleGroup
                key={groupId}
                group={group}
                dbuu={dbuu}
                groupId={groupId}
                expandedLayers={expandedLayers}
                setExpandedLayers={setExpandedLayers}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const DrcViewer = ({ onClose }) => {
  const [drcData, setDrcData] = useState(null);
  const [precision, setPrecision] = useState(1);
  // const [dbuu, setDbuu] = useState(1);
  const [highlightColor, setHighlightColor] = useState("#FF0000"); // Default highlight color");
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRules, setExpandedRules] = useState(new Set());
  const [expandedLayers, setExpandedLayers] = useState(new Set());
  const newuser=window.new_usersize;
  const newdb=window.new_dbsize;
  const dbuu=newuser*newdb || 1;


  useEffect(() => {
  function onDrcLoaded(e) {
    const payload = e.detail || {};
    const text = payload.raw || "";
    let jsonData = payload.json;
    if (!jsonData) {
      try { jsonData = JSON.parse(text); } catch (err) { console.error("Invalid DRC JSON", err); return; }
    }

    const ruleMap = new Map();
    jsonData.rules?.forEach(rule => {
      if (!ruleMap.has(rule.rule_name)) ruleMap.set(rule.rule_name, []);
      ruleMap.get(rule.rule_name).push(rule);
    });

    setDrcData({
      ...jsonData,
      groupedRules: Array.from(ruleMap.entries()).map(([ruleName, groups]) => ({ ruleName, groups }))
    });
  }
  window.addEventListener("drc-file-loaded", onDrcLoaded);
  return () => window.removeEventListener("drc-file-loaded", onDrcLoaded);
}, []);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);
        const ruleMap = new Map();

        jsonData.rules?.forEach(rule => {
          if (!ruleMap.has(rule.rule_name)) ruleMap.set(rule.rule_name, []);
          ruleMap.get(rule.rule_name).push(rule);
        });

        setDrcData({
          ...jsonData,
          groupedRules: Array.from(ruleMap.entries()).map(([ruleName, groups]) => ({
            ruleName,
            groups
          }))
        });
        // const dbuu = newuser*newdb; // fallback
        // console.log('dbuu calculated',dbuu);
        // setDbuu(dbuu);
      } catch (err) {
        console.error('Invalid JSON:', err);
      }
    };
    reader.readAsText(file);
  };
  console.log('dbu value',dbuu);
  console.log('new_dbsize value',newdb);
  console.log('new_usersize value',newuser);
  const totalViolations = drcData?.rules?.reduce((sum, rule) => sum + (rule.violations?.length || 0), 0) || 0;

  return (
    <div className="drc-viewer">
      <div className="viewer-header">
        <label htmlFor="file-upload" className="icon-button" title="Upload DRC Results">
          <UploadIcon />
        </label>
        <input
          id="file-upload"
          type="file"
          accept=".err"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
        />
        <span className="cell-name" title={drcData?.topcell_name || ''}>
          {drcData?.topcell_name || 'Topcell Name'}
        </span>
        <span className="violation-total" title={`${totalViolations} Total Violations`}>
          {totalViolations}
        </span>
        <button className="close-button" onClick={onClose}>X</button>
        
      </div>

      <div className="viewer-footer-icons">
        {/* <HighlightOutlined
        titleAccess="Highlight"
        onClick={() => {
    if (!drcData || !window.canvasEngine) return;
    const violationsToDraw = [];
    const drawnRuleNames = [];

    drcData.groupedRules.forEach(({ ruleName, groups }) => {
      if (expandedRules.has(ruleName)) {
        groups.forEach((group, idx) => {
          const groupId = `${ruleName}__${idx}`;
          if (expandedLayers.has(groupId)) {
            group.violations?.forEach(v => {
              violationsToDraw.push({
                ...v,
                rule_name: ruleName,
                group_index: idx, // ✅ tag with group index
              });
            });
            drawnRuleNames.push(ruleName);
          }
        });
      }
    });

    if (violationsToDraw.length > 0) {
      window.canvasEngine.drc.violations = violationsToDraw;
      window.canvasEngine.drc.drawnRuleNames = drawnRuleNames;
      window.canvasEngine.drc.drawAllViolations();
    }
        }}
        style={{ cursor: 'pointer' }}
      />


        <HighlightIcon
        titleAccess="Highlight ALL"
        onClick={() => {
      if (!window.canvasEngine || !drcData) return;

      const allViolations = [];

    drcData.groupedRules.forEach(({ ruleName, groups }) => {
      groups.forEach((group, idx) => {
        group.violations?.forEach(v => {
          allViolations.push({
            ...v,
            rule_name: ruleName,
            group_index: idx, // ✅ inject group index here too
          });
        });
      });
    });

    window.canvasEngine.drc.violations = allViolations;
    window.canvasEngine.drc.drawAllViolations();
      }}
      style={{ cursor: 'pointer' }}
      /> */}
      <HighlightOutlined
  titleAccess="Highlight"
  onClick={() => {
    if (!drcData || !window.canvasEngine) return;

    const newUserSize = 1/window.newUserSize || 1;

    const violationsToDraw = [];
    const drawnRuleNames = [];

    drcData.groupedRules.forEach(({ ruleName, groups }) => {
      if (expandedRules.has(ruleName)) {
        groups.forEach((group, idx) => {
          const groupId = `${ruleName}__${idx}`;
          if (expandedLayers.has(groupId)) {
            group.violations?.forEach(v => {
              violationsToDraw.push({
                ...v,
                rule_name: ruleName,
                group_index: idx,
                location: v.location?.map(([x, y]) => [
                  (x / dbuu) * (newUserSize),
                  (y / dbuu) * (newUserSize),
                ])
              });
            });
            drawnRuleNames.push(ruleName);
          }
        });
      }
    });

    if (violationsToDraw.length > 0) {
      window.canvasEngine.drc.violations = violationsToDraw;
      window.canvasEngine.drc.drawnRuleNames = drawnRuleNames;
      window.canvasEngine.drc.drawAllViolations();
    }
  }}
  style={{ cursor: 'pointer' }}
/>

<HighlightIcon
  titleAccess="Highlight ALL"
  onClick={() => {
    if (!window.canvasEngine || !drcData) return;

    const newUserSize =1/window.newUserSize || 1;

    const allViolations = [];

    drcData.groupedRules.forEach(({ ruleName, groups }) => {
      groups.forEach((group, idx) => {
        group.violations?.forEach(v => {
          allViolations.push({
            ...v,
            rule_name: ruleName,
            group_index: idx,
            location: v.location?.map(([x, y]) => [
              (x / dbuu) * (newUserSize),
              (y / dbuu) * (newUserSize),
            ])
          });
        });
      });
    });

    window.canvasEngine.drc.violations = allViolations;
    window.canvasEngine.drc.drawAllViolations();
  }}
  style={{ cursor: 'pointer' }}
/>



        <ClearAllIcon
          titleAccess="Clear ALL"
          onClick={() => {
            if (window.canvasEngine?.drc?.clearViolations) {
              window.canvasEngine.drc.clearViolations();
            }
          }}
          style={{ cursor: 'pointer' }}
        />

        <BackspaceOutlined
          titleAccess="Clear"
          onClick={() => {
            if (window.canvasEngine?.drc?.clearViolations) {
              const rulesToClear = [];

              drcData.groupedRules.forEach(({ ruleName, groups }) => {
                if (expandedRules.has(ruleName)) {
                  groups.forEach((_, idx) => {
                    const groupId = `${ruleName}__${idx}`;
                    if (expandedLayers.has(groupId)) {
                      rulesToClear.push(groupId);
                    }
                  });
                }
              });

              if (rulesToClear.length > 0) {
                window.canvasEngine.drc.clearViolations(rulesToClear);
              }
            }
          }}
          style={{ cursor: 'pointer' }}
        />

        <ArrowBackIcon
          titleAccess="Highlight Next"
          onClick={() => {
            if (window.canvasEngine?.drc?.highlightNextViolation) {
              window.canvasEngine.drc.highlightNextViolation();
            }
          }}
          style={{ cursor: 'pointer' }}
        />

        <ArrowForwardIcon
          titleAccess="Highlight Previous"
          onClick={() => {
            if (window.canvasEngine?.drc?.highlightPrevViolation) {
              window.canvasEngine.drc.highlightPrevViolation();
            }
          }}
          style={{ cursor: 'pointer' }}
        />

        <input
          type="color"
          className="highlight-color-picker"
          title="Choose highlight color"
          value={highlightColor}
          onChange={(e) => {
            const color = e.target.value;
            setHighlightColor(color);
            if (window.canvasEngine?.drc?.setHighlightColor) {
              window.canvasEngine.drc.setHighlightColor(color);
            }
          }}
        />

        <RestartAltRounded
          titleAccess="Reset Viewer"
          onClick={() => {
            // Reset all state
            setDrcData(null);
            setExpandedRules(new Set());
            setExpandedLayers(new Set());
            setSearchTerm('');
            setHighlightColor("#FF0000");

            // Clear canvas
            if (window.canvasEngine?.drc?.clearViolations) {
              window.canvasEngine.drc.clearViolations();
            }
          }}
          style={{ cursor: 'pointer' }}
        />
      </div>

      <div className="viewer-searchbar">
        <input
          className="search-input"
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
        />
      </div>

      <div className="viewer-content">
        {drcData?.groupedRules?.length > 0 ? (
          drcData.groupedRules
            .filter(({ ruleName, groups }) => {
              if (!searchTerm) return true;
              const matchRule = ruleName.toLowerCase().includes(searchTerm);
              const matchGroup = groups.some(group =>
                group.explanation?.toLowerCase().includes(searchTerm) ||
                group.value?.toLowerCase().includes(searchTerm) ||
                group.layer_name?.toLowerCase().includes(searchTerm)
              );
              return matchRule || matchGroup;
            })
            .map(({ ruleName, groups }, idx) => (
              <RuleItem
                key={idx}
                ruleName={ruleName}
                ruleGroups={groups}
                expandedRules={expandedRules}
                setExpandedRules={setExpandedRules}
                dbuu={dbuu}
                expandedLayers={expandedLayers}
                setExpandedLayers={setExpandedLayers}
              />
            ))
        ) : (
          <div className="viewer-empty">
            {drcData ? 'No DRC rules found' : 'Upload a JSON file to view DRC rules'}
          </div>
        )}
      </div>
    </div>
  );
};

export default DrcViewer;

